import React, { useCallback } from 'react';
import { PropTypes as PT } from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import _get from 'lodash/get';
import clsx from 'clsx';
import Logger from 'js-logger';
import { useDispatch, useSelector } from 'react-redux';
import { streamActions, featureSelectors } from '@lifesize/nucleus';
import { ReactComponent as MoreIcon } from '@lifesize/ux-assets/vectors/svgIcons/more.svg';
import { ReactComponent as RecordIcon } from '@lifesize/ux-assets/vectors/svgIcons/record.svg';
import { ReactComponent as RecordStopIcon } from '@lifesize/ux-assets/vectors/svgIcons/record-stop.svg';
import { ReactComponent as VideoIcon } from '@lifesize/ux-assets/vectors/svgIcons/video.svg';
import { ReactComponent as VideoOffIcon } from '@lifesize/ux-assets/vectors/svgIcons/video-off.svg';
import Tooltip from 'components/Tooltip/Tooltip';
import DialPadComponent from 'components/DialPad/DialPad';
import RaiseHandButton from 'components/InCall/InCallFooterButtons/RaiseHandButton';
import { MUTE_VIDEO, STOP_RECORDING, START_RECORDING } from 'constants/lsqaConstants';
import { FLAG_VIRTUAL_BACKGROUND } from 'constants/FeatureFlags';
import PresentationButton from 'containers/InCall/PresentationButton/PresentationButtonContainer';
import OtherOptions from 'containers/InCall/OtherOptions/OtherOptionsContainer';
import FarEndCameraControls from 'containers/FarEndCameraControls/FarEndCameraControlsContainer';
import AudioButton from 'containers/AudioButton/AudioButtonContainer';
import EndCallButton from 'containers/InCall/EndCallButtonContainer';
import CallDuration from './CallDuration';
import { vbbManager } from 'utils/vbbManager';
import { readFromLocalStorage } from 'utils/localStorage/vbbImages';
import { LsKey } from 'utils/localStorageUtils';
import classes from './CallFooter.module.scss';

const CallFooter = (props) => {
  const vbbSettings = useSelector((state) => _get(state, 'app.vbbSettings'));
  const dispatch = useDispatch();
  const { enabled: vbbEnabled, selection: bgSelection } = vbbSettings;
  const vbbFeatureEnabled = useSelector((state) => featureSelectors.getFeatureFlag(state)(FLAG_VIRTUAL_BACKGROUND));

  const handleVideoMuteClick = useCallback(
    async (newMuteState) => {
      const { [LsKey.VbbDefault]: bgDefault } = readFromLocalStorage();
      const hasDefaultVbbBg = !!bgDefault; // the default background id is saved in localStorage
      const bgSelectionId = _get(bgSelection, 'id');
      const hasBackgroundSelected = bgSelectionId && bgSelectionId !== 'none';
      const useVbb = vbbEnabled || (hasDefaultVbbBg && hasBackgroundSelected);
      // If VBB enabled, init it before unmuting
      if (!newMuteState && vbbFeatureEnabled && useVbb) {
        Logger.debug('video (un)mute: initialize vbb');
        // Stream change observer will activate vbb
        vbbManager.setVbbEnabledWithoutStarting(dispatch);
      }

      // This was originally done in InCallFooterContainer, w/ onVideoMuteClick() prop
      await dispatch(streamActions.toggleVideoMute(newMuteState));
    },
    [bgSelection, dispatch, vbbEnabled, vbbFeatureEnabled]
  );

  const renderVideoMuteButton = () => {
    let button = null;
    if (props.isConnected) {
      if (props.isVideoMuted) {
        button = (
          <button
            className={classes.inCallButtonActive}
            onClick={() => handleVideoMuteClick(false)}
            {...MUTE_VIDEO}
            data-testid="unmute-video-btn"
          >
            <VideoOffIcon data-testid="videoOffIcon" />
            <Tooltip
              location="top"
              style={{ bottom: '100%', marginBottom: 15 }}
              label={
                <FormattedMessage
                  id="components.incall.controls.videoMute.tooltip.on"
                  defaultMessage="Turn camera on"
                />
              }
            />
          </button>
        );
      } else {
        const activeVideoClasses = [classes.inCallButton, classes.activeVideoButton].join(' ');

        button = (
          <button
            className={activeVideoClasses}
            onClick={() => handleVideoMuteClick(true)}
            {...MUTE_VIDEO}
            data-testid="mute-video-btn"
          >
            <VideoIcon data-testid="videoIcon" />
            <Tooltip
              location="top"
              style={{ bottom: '100%', marginBottom: 15 }}
              label={
                <FormattedMessage
                  id="components.incall.controls.videoMute.tooltip.off"
                  defaultMessage="Turn camera off"
                />
              }
            />
          </button>
        );
      }
    }
    return button;
  };

  const renderRecordingButton = () => {
    let button = null;
    if (props.isConnected && !props.isGuest && props.recordingEnabled) {
      if (props.isRecording) {
        let label;

        if (props.canStopRecording) {
          label = (
            <FormattedMessage id="components.incall.controls.record.tooltip.off" defaultMessage="Stop recording" />
          );
        } else if (props.recorder) {
          label = (
            <FormattedMessage
              id="components.incall.footer.recording.cantStop"
              defaultMessage="Recording started by {name}"
              values={{
                name: props.recorder
              }}
            />
          );
        } else {
          label = (
            <FormattedMessage
              id="components.incall.footer.recording.cantStopUnknown"
              defaultMessage="Recording started"
            />
          );
        }

        button = (
          <button
            className={clsx(classes.inCallButtonActive, classes.recordingButton)}
            onClick={() => props.onStopRecording()}
            disabled={!props.canStopRecording}
            {...STOP_RECORDING}
            data-testid="stop-recording-btn"
          >
            <RecordStopIcon className={classes.recordStop} data-testid="recordStopIcon" />
            <Tooltip location="top" style={{ bottom: '100%', marginBottom: 15 }} label={label} />
          </button>
        );
      } else {
        button = (
          <button
            className={clsx(classes.inCallButton, classes.recordingButton)}
            onClick={() => props.onStartRecording()}
            disabled={!props.inCall}
            {...START_RECORDING}
            data-testid="start-recording-btn"
          >
            <RecordIcon data-testid="recordIcon" />
            <Tooltip
              location="top"
              style={{ bottom: '100%', marginBottom: 15 }}
              label={
                <FormattedMessage id="components.incall.controls.record.tooltip.on" defaultMessage="Start recording" />
              }
            />
          </button>
        );
      }
    }
    return button;
  };

  const renderKebabButton = () => {
    let button = null;
    if (props.isConnected) {
      button = (
        <div className={classes.kebabContainer}>
          <button
            className={classes.kebabButton}
            data-testid="kebab-btn"
            id="inCallKebabBtn"
            onClick={() => props.onKebabClicked()}
          >
            <MoreIcon data-testid="moreIcon" />
          </button>
          <OtherOptions />
        </div>
      );
    }
    return button;
  };

  function renderCenterContainer() {
    const centerContainerClasses = clsx(classes.centerContainer, !props.showRaiseHandsButton && classes.compact);
    return props.isMinimized ? (
      <div className={classes.minimizedCenterContainer}>
        <AudioButton iconClass={classes.icon} />
        {props.isConnected && <EndCallButton />}
      </div>
    ) : (
      <div className={centerContainerClasses}>
        <AudioButton iconClass={classes.icon} />
        {renderVideoMuteButton()}
        <div className={classes.verticalDivide} />
        <PresentationButton />
        {renderRecordingButton()}
        {props.showRaiseHandsButton && (
          <RaiseHandButton
            disabled={!props.canRaiseHand}
            handIsProcessing={props.handIsProcessing}
            handIsRaised={props.handIsRaised}
            isConnected={props.isConnected}
            notification={props.btnNotificationRaisedHand}
            onRaiseHand={props.onRaiseHand}
          />
        )}
      </div>
    );
  }

  function renderRightContainer() {
    return props.isMinimized ? (
      <div className={classes.minimizedRightContainer}>
        <CallDuration startTime={props.startTime} />
      </div>
    ) : (
      <div className={classes.rightContainer}>
        <CallDuration startTime={props.startTime} />
        {props.isConnected && <EndCallButton />}
        {renderKebabButton()}
        {props.showCameraControls && <FarEndCameraControls />}
        <DialPadComponent
          isVisible={props.showDialPad}
          handleClose={props.toggleDialPad}
          addNumber={props.onDTMFClicked}
        />
      </div>
    );
  }

  return (
    <div className={props.isMinimized ? classes.minimizedFooterContainer : classes.footerContainer}>
      <div style={{ flex: 1 }} />
      {renderCenterContainer()}
      {renderRightContainer()}
    </div>
  );
};

CallFooter.propTypes = {
  isVideoMuted: PT.bool,
  startTime: PT.number,
  recordingEnabled: PT.bool,
  btnNotificationRaisedHand: PT.object,
  canRaiseHand: PT.bool,
  canStopRecording: PT.bool,
  handIsProcessing: PT.bool,
  handIsRaised: PT.bool,
  intl: PT.object,
  isConnected: PT.bool.isRequired,
  isGuest: PT.bool,
  isMinimized: PT.bool,
  isRecording: PT.bool,
  onDTMFClicked: PT.func.isRequired,
  onKebabClicked: PT.func.isRequired,
  onRaiseHand: PT.func.isRequired,
  onStartRecording: PT.func.isRequired,
  onStopRecording: PT.func.isRequired,
  onVideoMuteClick: PT.func.isRequired, // JEFFTODO: deprecate
  pipExpanded: PT.bool.isRequired,
  recorder: PT.string,
  selfPresenting: PT.bool.isRequired,
  showCameraControls: PT.bool,
  showDialPad: PT.bool.isRequired,
  toggleDialPad: PT.func.isRequired,
  togglePip: PT.func.isRequired,
  videoElementId: PT.string.isRequired,
  videoLayout: PT.object,
  videoPresentationId: PT.string.isRequired,
  videoWidth: PT.number,
  showRaiseHandsButton: PT.bool
};

export default injectIntl(CallFooter);
